/**
 * REF-5796 (Option 3) — real "Save to Photos / Files" of the *chosen* format on iOS.
 *
 * iOS Safari only permits a save (`navigator.share`, `<a download>`, `window.open`) during the
 * ~0.5s transient-activation window after a tap. Any `await` (fetch, re-encode) blows past it, so
 * the trick is to remove all async from the tap handler: **prepare the image ahead of the tap**
 * (fetch + decode when the menu opens), then on the tap synchronously re-encode + hand off. No
 * API/store imports here so it stays unit-testable and gesture-cheap.
 */

type RasterFormat = "png" | "jpg" | "webp";

const MIME: Record<RasterFormat, string> = {
  png: "image/png",
  jpg: "image/jpeg",
  webp: "image/webp",
};

/** Infer an image MIME type from a URL's extension (default png). */
const inferImageMime = (url: string): string => {
  const ext = url.split(/[?#]/)[0].split(".").pop()?.toLowerCase();
  if (ext === "jpg" || ext === "jpeg") return "image/jpeg";
  if (ext === "webp") return "image/webp";

  return "image/png";
};

export interface PreparedImage {
  /** Decoded, same-origin (blob object URL) image — safe to draw to a canvas without tainting it. */
  img: HTMLImageElement;
  /** The source bytes, reused verbatim when the requested format already matches (lossless). */
  blob: Blob;
  /** Source MIME type. */
  type: string;
  /** Object URL backing `img`; revoke when the prepared image is discarded. */
  objectUrl: string;
}

/**
 * Fetch + decode `url` so it can be re-encoded and shared/downloaded **synchronously** later
 * (inside a user gesture). Call this ahead of the tap — e.g. when the download menu opens.
 * Fetching as a blob and loading via an object URL keeps the `<img>` same-origin, so drawing it
 * to a canvas doesn't taint it. Returns `null` on any failure (caller falls back).
 */
export const prepareImageForShare = async (url: string): Promise<PreparedImage | null> => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      return null;
    }

    const rawBlob = await response.blob();
    if (rawBlob.size === 0) {
      return null;
    }

    // GCS serves some images as `application/octet-stream`. A blob object URL with a non-image
    // MIME won't decode in an <img> on Safari (`img.decode()` rejects), so re-type it to a real
    // image MIME inferred from the URL before creating the object URL.
    const type = rawBlob.type.startsWith("image/") ? rawBlob.type : inferImageMime(url);
    const blob = type === rawBlob.type ? rawBlob : new Blob([rawBlob], { type });

    const objectUrl = URL.createObjectURL(blob);
    const img = new Image();
    img.src = objectUrl;
    await img.decode();

    return { img, blob, type, objectUrl };
  } catch {
    return null;
  }
};

/** Release the object URL backing a prepared image. Safe to call with null. */
export const revokePreparedImage = (prepared: PreparedImage | null): void => {
  if (prepared) {
    try {
      URL.revokeObjectURL(prepared.objectUrl);
    } catch {
      // ignore — best-effort cleanup
    }
  }
};

/** Synchronously decode a data: URL to a Blob (no fetch → safe inside a gesture). */
const dataUrlToBlob = (dataUrl: string): Blob => {
  const [header, data] = dataUrl.split(",");
  const mime = header.match(/^data:([^;]+)/)?.[1] || "image/png";
  const binary = atob(data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  return new Blob([bytes], { type: mime });
};

/**
 * Deliver a prepared image in `format` via the native share sheet (iOS/Android → "Save to Photos"
 * / "Save to Files") or, where that's unavailable, a blob download. **Must be called synchronously
 * inside the user's gesture** — no `await` before it — which is why the image was prepared ahead of
 * time. Re-encodes only when the source format differs (otherwise ships the original bytes,
 * lossless). Returns true if a save was initiated, false if it couldn't (caller falls back).
 */
export const shareOrDownloadImage = (prepared: PreparedImage, format: RasterFormat, baseName: string): boolean => {
  try {
    const mime = MIME[format];
    let outBlob: Blob;

    if (prepared.type === mime) {
      // Source already in the requested format — ship it untouched (lossless, no canvas).
      outBlob = prepared.blob;
    } else {
      const canvas = document.createElement("canvas");
      canvas.width = prepared.img.naturalWidth;
      canvas.height = prepared.img.naturalHeight;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        return false;
      }

      // JPEG has no alpha — flatten transparency onto white so PNGs don't export black.
      if (mime === "image/jpeg") {
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      ctx.drawImage(prepared.img, 0, 0);
      outBlob = dataUrlToBlob(canvas.toDataURL(mime, 0.95));
    }

    const filename = `${baseName}.${format}`;
    const file = new File([outBlob], filename, { type: mime });

    // Prefer the native share sheet — the iOS-idiomatic "Save to Photos / Files".
    const nav = navigator as Navigator & { canShare?: (data: { files: File[] }) => boolean; share?: (data: { files: File[] }) => Promise<void> };
    if (typeof nav.share === "function" && nav.canShare?.({ files: [file] })) {
      // The call is synchronous (in-gesture); the returned promise settling later is fine.
      void nav.share({ files: [file] }).catch(() => {
        // user cancelled the share sheet, or it failed — nothing to do
      });

      return true;
    }

    // Fallback: a real blob download (iOS Download Manager / desktop).
    const objectUrl = URL.createObjectURL(outBlob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = filename;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    return true;
  } catch {
    return false;
  }
};

/** Source format of a prepared image (for the plain "Download" action, which doesn't re-encode). */
export const preparedImageFormat = (prepared: PreparedImage): RasterFormat => {
  if (prepared.type === "image/jpeg") return "jpg";
  if (prepared.type === "image/webp") return "webp";

  return "png";
};
